import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { productionReportAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { 
  Loader2, Download, Calendar, Building2, DollarSign, Users, 
  CreditCard, Banknote, ArrowRightLeft, FileSpreadsheet, Filter 
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, startOfYear, subMonths } from 'date-fns';
import * as XLSX from 'xlsx';

const ProductionReport = () => {
  const [loading, setLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    industries: [],
    referralSources: [],
    staff: [],
    services: []
  });
  
  // Filter state
  const [startDate, setStartDate] = useState(format(startOfMonth(subMonths(new Date(), 11)), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
  const [serviceType, setServiceType] = useState('all');
  const [industry, setIndustry] = useState('all');
  const [assignedStaff, setAssignedStaff] = useState('all');
  const [referralSource, setReferralSource] = useState('all');
  const [groupBy, setGroupBy] = useState('month');
  const [ytd, setYtd] = useState(false);
  
  // Report data
  const [reportData, setReportData] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const fetchFilterOptions = async () => {
    try {
      const response = await productionReportAPI.getFilterOptions();
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const generateReport = async () => {
    try {
      setLoading(true);
      const response = await productionReportAPI.getReport({
        startDate: new Date(startDate).toISOString(),
        endDate: new Date(endDate).toISOString(),
        serviceType: serviceType === 'all' ? null : serviceType,
        industry: industry === 'all' ? null : industry,
        assignedStaff: assignedStaff === 'all' ? null : assignedStaff,
        referralSource: referralSource === 'all' ? null : referralSource,
        groupBy,
        ytd
      });
      setReportData(response.data);
    } catch (error) {
      console.error('Error generating report:', error);
      toast({ title: 'Error', description: 'Failed to generate report', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatPeriod = (period) => {
    if (groupBy === 'month' && period.includes('-')) {
      const [year, month] = period.split('-');
      return format(new Date(parseInt(year), parseInt(month) - 1), 'MMMM yyyy');
    }
    return period;
  };

  const exportToExcel = () => {
    if (!reportData?.rows || reportData.rows.length === 0) {
      toast({ title: 'No Data', description: 'No data to export', variant: 'destructive' });
      return;
    }

    // Create header with filters
    const headerInfo = [
      ['Accounting Production Report'],
      ['Generated: ' + format(new Date(), 'MMMM dd, yyyy HH:mm:ss')],
      [''],
      ['Applied Filters:'],
      ['Date Range: ' + format(new Date(startDate), 'MMM dd, yyyy') + ' to ' + format(new Date(endDate), 'MMM dd, yyyy')],
      ['Group By: ' + groupBy.charAt(0).toUpperCase() + groupBy.slice(1)],
      serviceType !== 'all' ? ['Service Type: ' + serviceType] : [],
      industry !== 'all' ? ['Industry: ' + industry] : [],
      assignedStaff !== 'all' ? ['Assigned Staff: ' + assignedStaff] : [],
      referralSource !== 'all' ? ['Referral Source: ' + referralSource] : [],
      ytd ? ['YTD: Yes'] : [],
      ['']
    ].filter(row => row.length > 0);

    // Create data rows
    const dataRows = reportData.rows.map(row => ({
      'Period': formatPeriod(row.period),
      'Active Clients': row.activeClients,
      'Gross Production': row.grossProduction,
      'Net Production': row.netProduction,
      'GST/HST': row.gstHst,
      'Cash Collections': row.cashCollections,
      'E-Transfer Collections': row.etransferCollections,
      'Credit Card Collections': row.creditCardCollections,
      'AR Open': row.arOpen
    }));

    // Add totals row
    dataRows.push({
      'Period': 'TOTALS',
      'Active Clients': reportData.totals.activeClients,
      'Gross Production': reportData.totals.grossProduction,
      'Net Production': reportData.totals.netProduction,
      'GST/HST': reportData.totals.gstHst,
      'Cash Collections': reportData.totals.cashCollections,
      'E-Transfer Collections': reportData.totals.etransferCollections,
      'Credit Card Collections': reportData.totals.creditCardCollections,
      'AR Open': reportData.totals.arOpen
    });

    // Create workbook
    const wb = XLSX.utils.book_new();
    
    // Create header worksheet first
    const ws = XLSX.utils.aoa_to_sheet(headerInfo);
    
    // Add data starting after headers
    XLSX.utils.sheet_add_json(ws, dataRows, { origin: 'A' + (headerInfo.length + 1) });

    // Set column widths
    ws['!cols'] = [
      { wch: 20 },  // Period
      { wch: 15 },  // Active Clients
      { wch: 18 },  // Gross Production
      { wch: 18 },  // Net Production
      { wch: 15 },  // GST/HST
      { wch: 18 },  // Cash Collections
      { wch: 22 },  // E-Transfer Collections
      { wch: 22 },  // Credit Card Collections
      { wch: 15 }   // AR Open
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Production Report');
    
    const filename = `Production_Report_${format(new Date(), 'yyyy-MM-dd_HHmm')}.xlsx`;
    XLSX.writeFile(wb, filename);
    toast({ title: 'Success', description: 'Report exported to Excel' });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Accounting Production Report</h1>
        <p className="text-gray-500 mt-1">Production based on invoice date, Collections based on payment date</p>
      </div>

      {/* Filters Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Report Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Date Range */}
            <div className="space-y-2">
              <Label>Start Date *</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="start-date"
              />
            </div>
            <div className="space-y-2">
              <Label>End Date *</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="end-date"
              />
            </div>

            {/* Service Type */}
            <div className="space-y-2">
              <Label>Service Type</Label>
              <Select value={serviceType} onValueChange={setServiceType}>
                <SelectTrigger>
                  <SelectValue placeholder="All Services" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Services</SelectItem>
                  {filterOptions.services.map(service => (
                    <SelectItem key={service} value={service}>{service}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Industry */}
            <div className="space-y-2">
              <Label>Industry</Label>
              <Select value={industry} onValueChange={setIndustry}>
                <SelectTrigger>
                  <SelectValue placeholder="All Industries" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Industries</SelectItem>
                  {filterOptions.industries.map(ind => (
                    <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Assigned Staff */}
            <div className="space-y-2">
              <Label>Assigned Staff</Label>
              <Select value={assignedStaff} onValueChange={setAssignedStaff}>
                <SelectTrigger>
                  <SelectValue placeholder="All Staff" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff</SelectItem>
                  {filterOptions.staff.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Referral Source */}
            <div className="space-y-2">
              <Label>Referral Source</Label>
              <Select value={referralSource} onValueChange={setReferralSource}>
                <SelectTrigger>
                  <SelectValue placeholder="All Sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {filterOptions.referralSources.map(source => (
                    <SelectItem key={source} value={source}>{source}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Group By */}
            <div className="space-y-2">
              <Label>Group By</Label>
              <Select value={groupBy} onValueChange={setGroupBy}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Month</SelectItem>
                  <SelectItem value="service">Service</SelectItem>
                  <SelectItem value="industry">Industry</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="referral_source">Referral Source</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* YTD Toggle */}
            <div className="space-y-2">
              <Label>&nbsp;</Label>
              <div className="flex items-center gap-2 h-10">
                <Checkbox
                  id="ytd"
                  checked={ytd}
                  onCheckedChange={setYtd}
                />
                <Label htmlFor="ytd">Year to Date (YTD)</Label>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button onClick={generateReport} disabled={loading} className="gap-2" data-testid="generate-report-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
              Generate Report
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Report Results */}
      {reportData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Active Clients</p>
                    <p className="text-xl font-bold">{reportData.totals.activeClients}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <DollarSign className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Gross Production</p>
                    <p className="text-xl font-bold">{formatCurrency(reportData.totals.grossProduction)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <CreditCard className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total Collections</p>
                    <p className="text-xl font-bold">
                      {formatCurrency(
                        reportData.totals.cashCollections + 
                        reportData.totals.etransferCollections + 
                        reportData.totals.creditCardCollections
                      )}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <Building2 className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">AR Open</p>
                    <p className="text-xl font-bold">{formatCurrency(reportData.totals.arOpen)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Report Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Production Details</span>
                <Button variant="outline" size="sm" onClick={exportToExcel} className="gap-2" data-testid="export-excel-btn">
                  <Download className="h-4 w-4" />
                  Export Excel
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">Period</TableHead>
                      <TableHead className="font-semibold text-center">Active Clients</TableHead>
                      <TableHead className="font-semibold text-right">Gross Production</TableHead>
                      <TableHead className="font-semibold text-right">Net Production</TableHead>
                      <TableHead className="font-semibold text-right">GST/HST</TableHead>
                      <TableHead className="font-semibold text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Banknote className="h-4 w-4" />
                          Cash
                        </div>
                      </TableHead>
                      <TableHead className="font-semibold text-right">
                        <div className="flex items-center justify-end gap-1">
                          <ArrowRightLeft className="h-4 w-4" />
                          E-Transfer
                        </div>
                      </TableHead>
                      <TableHead className="font-semibold text-right">
                        <div className="flex items-center justify-end gap-1">
                          <CreditCard className="h-4 w-4" />
                          Credit Card
                        </div>
                      </TableHead>
                      <TableHead className="font-semibold text-right">AR Open</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.rows.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{formatPeriod(row.period)}</TableCell>
                        <TableCell className="text-center">{row.activeClients}</TableCell>
                        <TableCell className="text-right">{formatCurrency(row.grossProduction)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(row.netProduction)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(row.gstHst)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(row.cashCollections)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(row.etransferCollections)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(row.creditCardCollections)}</TableCell>
                        <TableCell className="text-right text-red-600">{formatCurrency(row.arOpen)}</TableCell>
                      </TableRow>
                    ))}
                    {/* Totals Row */}
                    <TableRow className="bg-gray-100 font-bold">
                      <TableCell>TOTALS</TableCell>
                      <TableCell className="text-center">{reportData.totals.activeClients}</TableCell>
                      <TableCell className="text-right">{formatCurrency(reportData.totals.grossProduction)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(reportData.totals.netProduction)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(reportData.totals.gstHst)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(reportData.totals.cashCollections)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(reportData.totals.etransferCollections)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(reportData.totals.creditCardCollections)}</TableCell>
                      <TableCell className="text-right text-red-600">{formatCurrency(reportData.totals.arOpen)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
              {reportData.rows.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No data found for the selected filters
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default ProductionReport;
